import React, {Suspense, useEffect, useState} from 'react'
import { useEditorContext } from '../../../../../../../contexts/Editor';
import TextInputField from '../../../../../common/TextField/TextInputField';
import { Badge, Button, TabNavigation, TabNavigationItem } from '@livingdesign/react';
import TemplateGridItem from './TemplateGridItem';
import "./EmailTemplateListContainer.scss";
import TemplateGalleryPreview from '../../../../../../template/TemplateGalleryPreview';
import { copyTemplateFromGallery } from '../../../../../../../utils/template';
import { useAppContext } from '../../../../../../../contexts/Core';
import EmailTemplateEditorSkeleton from '../../../../../../template/EmailEditor/EmailTemplateEditorSkeleton';
import { ERROR_MSG, SNACK_TYPES } from '../../../../../../../consts/Common';
import { useMessageDetailsForm } from '../../../../../../../contexts/MessageDetailsForm';
import { TEMPLATE } from '../../../../../../../consts/DBFields';
import { exportMjml } from '../../../../../../template/EmailEditor/grapesjs/util';
import { useCampaignContext } from '../../../../../../../contexts/Campaign';

export const TemplateEditor = React.lazy(() => import("../../../../../../template/TemplateEditor"));

export const QA_ID_PREFIX = "EmailTemplateListContainer";

function EmailTemplateListContainer({
  channel,
  selectedCampaign,
  selectedTemplate,
  saveTemplate,
  saveButtonText,
  closeEditor,
  setShowActionsFooter,
}) {

  const {
    setCampaignError,
  } = useCampaignContext();

  const {
    activeGalleryType,
    activeTemplateTab,
    setActiveTemplateTab,
    templateTabs,
    setActiveBaseTemplate,
    searchPhrase,
    setSearchPhrase,
    filteredGalleries,
  } = useEditorContext();

  const {
    setTemplatePreviewDetails,
    setShowTemplateOverlay,
    setAlertModel,
    setError,
    setGlobalErrorMessage,
    errors,
  } = useMessageDetailsForm();

  const { setSnackMessage, setSnackType, selectedTeam } = useAppContext();

  const [templatesToDisplay, setTemplatesToDisplay] = useState([]);
  const [recentlyUsedTemplates, setRecentlyUsedTemplates] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [loadingTemplateInEditor, toggleLoadingTemplateInEditor] = useState(false);
  const [editorContent, setEditorContent] = useState(null);
  const [previousContent, setPreviousContent] = useState(null);
  const [unsavedChanges, toggleUnsavedChanges] = useState(false);

  // State to store the actual GrapesJS editor instance
  const [grapesJSEditor, setGrapesJSEditor] = useState(null);

  useEffect(() => {
    setTabs(templateTabs.filter(tab => tab.label !== "Previously Used"));
    setRecentlyUsedTemplates(filteredGalleries[filteredGalleries.length - 1] || []);
  }, []);

  useEffect(() => {
    setTemplatesToDisplay(filteredGalleries[activeTemplateTab] || []);
  }, [filteredGalleries, activeTemplateTab]);

  useEffect(() => {
    setEditorContent(selectedTemplate?.[TEMPLATE.CONTENT]?.trim());
  }, [selectedTemplate]);

  useEffect(() => {
    if(editorContent) {
      setShowActionsFooter(false);
    } else if ((!editorContent && previousContent)) {
      setShowActionsFooter(false);
    } else {
      setShowActionsFooter(true);
    }
  }, [editorContent]);

  // Helper function to get current editor content (similar to getCurrentContent in TemplateOverlay)
  const getCurrentEditorContent = () => {
    const mjml = exportMjml(grapesJSEditor);
    if(!mjml) {
      return editorContent;
    }else if (mjml.error) {
      return editorContent;
    } else {
      return mjml.content;
    }
  };

  // Function to open content in editor with backup capability
  const openInEditor = (content) => {
    toggleUnsavedChanges(true);
    setEditorContent(content);
    setPreviousContent(null);
  }

  // Function to handle template selection with backup mechanism
  const selectTemplate = async (template, activeGalleryType)=>{
    toggleLoadingTemplateInEditor(true);
    try {
      await copyTemplateFromGallery(
        activeGalleryType,
        template,
        channel,
        (templateContent) => {
          toggleLoadingTemplateInEditor(false);
          openInEditor(templateContent);
        },
        selectedTeam
      );
      setShowActionsFooter(false);
    } catch (err) {
      console.log(err);
      
      toggleLoadingTemplateInEditor(false);
      setSnackMessage(ERROR_MSG.SELECT_TEMPLATE);
      setSnackType(SNACK_TYPES.ERROR);
    }
  }

  // Callback to receive the GrapesJS editor instance from TemplateEditor
  const handleEditorReady = (editor) => {
    console.log('GrapesJS Editor ready:', editor);
    setGrapesJSEditor(editor);
  };

  // Enhanced gallery navigation with restore capability
  const handleBackToGallery = () => {
    // Show option to restore previous content
    setAlertModel({
      show: true,
      handleClose: () => setAlertModel({ show: false }),
      title: "Are you sure you want to go back to the template gallery?",
      message: "All unsaved changes will be lost when you select a new template.",
      handleConfirm: () => {
        setEditorContent(null);
        setPreviousContent(getCurrentEditorContent());
        setAlertModel({ show: false });
      },
      handleCancel: () => {
        // restoreBackupContent();
        setAlertModel({ show: false });
      },
      confirmButtonText: "Yes, I'm sure",
      cancelButtonText: "Cancel",
    });
  };

  return (
    <>
    <section
      className='template-overlay-title'
      data-testid={`${QA_ID_PREFIX}-template-overlay-title`}
    >
        <div
          style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}
          data-testid={`${QA_ID_PREFIX}-title-row`}
        >
        <div data-testid={`${QA_ID_PREFIX}-title-text`}>
          <h2 data-testid={`${QA_ID_PREFIX}-choose-template-title`}>Choose a Template</h2>
          <p data-testid={`${QA_ID_PREFIX}-choose-template-description`}>
              Choose from ready-to-use templates to get started quickly. Each one is designed to fit common message types and help you keep communication clear and consistent.
          </p>
        </div>
        <div data-testid={`${QA_ID_PREFIX}-back-to-template-btn-container`}>
          {(!editorContent && previousContent) && <Button
            variant='primary'
            data-testid={`${QA_ID_PREFIX}-back-to-template-btn`}
            onClick={() => {
              setEditorContent(previousContent);
              setPreviousContent(null);
            }}
          >
            Back to Template
          </Button>}
        </div>
        </div>
      </section>
    <div
      className='email-template-list-container'
      style={{
        display: editorContent ? 'none' : 'block'
      }}
      data-testid={`${QA_ID_PREFIX}-email-template-list-container`}
    >
        <div
          style={{maxWidth: "700px", marginTop: "24px"}}
          data-testid={`${QA_ID_PREFIX}-search-input-container`}
        >
          <TextInputField
            value={searchPhrase}
            handleChange={(value) => setSearchPhrase(value)}
            placeholder="Search for a template"
            field={{
                placeholder: "Search for a template"
            }}
            data-testid={`${QA_ID_PREFIX}-search-input`}
          />  
        </div>
        <section
          className="recently-used-templates"
          data-testid={`${QA_ID_PREFIX}-recently-used-templates-section`}
        >
        <h3 data-testid={`${QA_ID_PREFIX}-recently-used-templates-title`}>Recently Used Templates</h3>
        <div
          className="template-grid"
          data-testid={`${QA_ID_PREFIX}-recently-used-templates-grid`}
        >
          {recentlyUsedTemplates.length === 0 && <div data-testid={`${QA_ID_PREFIX}-no-recently-used-templates`}>No recently used templates found.</div>}
          {recentlyUsedTemplates.map(template => (
            <TemplateGridItem
              key={template?.id}
              title={template?.name}
              previewHTML={template?.preview}
              onUseTemplate={() => setActiveBaseTemplate(template?.id)}
              data-testid={`${QA_ID_PREFIX}-recently-used-template-item-${template?.id}`}
            />
          ))}
        </div>
      </section>

      <section
        className="all-templates"
        data-testid={`${QA_ID_PREFIX}-all-templates-section`}
      >
        <h3 data-testid={`${QA_ID_PREFIX}-all-templates-title`}>All Templates</h3>
        <TabNavigation data-testid={`${QA_ID_PREFIX}-tab-navigation`}>
            {Object.values(tabs).map((tab, index) => {
              const label = tab.label;
              const count = filteredGalleries[index].length || 0
              return (
                <TabNavigationItem
                  key={index}
                  onClick={() => {setActiveTemplateTab(index)}}
                  isCurrent={index === activeTemplateTab}
                  data-testid={`${QA_ID_PREFIX}-tab-navigation-item-${label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {label}&nbsp;&nbsp;
                  <Badge data-testid={`${QA_ID_PREFIX}-tab-badge-${label.replace(/\s+/g, '-').toLowerCase()}`}>{count}</Badge>
                </TabNavigationItem>
              )
            })}
        </TabNavigation>
        <div
          className="template-grid"
          data-testid={`${QA_ID_PREFIX}-all-templates-grid`}
        >
          {templatesToDisplay.length === 0 && <div data-testid={`${QA_ID_PREFIX}-no-templates-found`}>No templates found.</div>}
          {templatesToDisplay.map(template => (
            <TemplateGridItem
                key={template?.id}
                title={template?.name}
                previewHTML={template?.preview}
                onUseTemplate={() => {
                  setTemplatePreviewDetails({
                    showPreview: true,
                    template: template,
                    title: template?.name,
                    selectTemplate: () => {
                      setActiveBaseTemplate(template?.id);
                      setTemplatePreviewDetails({ showPreview: false, template: null });
                      selectTemplate(template, activeGalleryType);
                    }
                  });
                }}
                data-testid={`${QA_ID_PREFIX}-all-template-item-${template?.id}`}
            />
          ))}
        </div>
      </section>
    </div>
    {editorContent && 
      <div
        className='template-editor-container'
        data-testid={`${QA_ID_PREFIX}-template-editor-container`}
      >
        <Suspense fallback={<EmailTemplateEditorSkeleton />}>
          <TemplateEditor
            templateContent={editorContent}
            channel={channel}
            saveTemplate={saveTemplate}
            saveAndExit={async () => {
              // Save the template and close the editor - callback
              setEditorContent(null);
              setShowTemplateOverlay(false);
              setGrapesJSEditor(null);
              if(setError) {
                setError("templateContent", null);
                if(errors && Object.keys(errors).length <= 1) {
                  setGlobalErrorMessage("");
                }
              }
            }}
            saveButtonText={saveButtonText}
            toggleUnsavedChanges={toggleUnsavedChanges}
            openGallery={() => {
              handleBackToGallery();
            }}
            selectedCampaign={selectedCampaign}
            selectedTemplate={selectedTemplate}
            setError={setCampaignError}
            // renameTemplate={renameTemplate}
            unsavedChanges={unsavedChanges}
            onEditorReady={handleEditorReady}  // Pass the callback to get the editor instance
            data-testid={`${QA_ID_PREFIX}-template-editor`}
          />
        </Suspense>
      </div>
    }
    </>
  )
}

export default EmailTemplateListContainer;